<template>
  <div class="container container-svg">
    <div
      class="color-editor-content row w-100 min-h-0"
      :class="{ 'editor-color-open': showEditColor }"
    >
      <div v-show="showEditColor" class="edit-color-container col-12 col-md-6 mb-4">
        <div class="controls-box">
          <h3 class="edit-title">
            {{ $t('main.preview.editColor.title') }}
            <b-button id="btn-tooltip" class="btn-tooltip"> i </b-button>
          </h3>
          <b-tooltip custom-class="custom-tooltip" target="btn-tooltip" placement="top">
            {{ $t('preview.svgEdit.main.editColorsTooltip') }}
          </b-tooltip>
          <div class="color-gradient-container">
            <div v-show="editColorPalette.length" id="colors">
              <p class="color-group-title">{{ $t('main.preview.editColor.text') }}</p>
              <div class="vx-container-colors">
                <div class="inputGroup">
                  <input
                    v-for="color in editColorPalette"
                    :id="color.id"
                    :key="color.id"
                    type="color"
                    :value="color.value"
                    :data-colorsinfo="color.id"
                    :data-colorsvalue="color.value"
                    @change="changeColor"
                    @input="moveChangeColor"
                  />
                </div>
              </div>
            </div>
            <div v-show="editColorGradients.length" id="gradients" class="gradients-section">
              <p class="gradient-group-title">{{ $t('main.preview.editColor.text2') }}</p>
              <div class="vx-container-colors">
                <div
                  v-for="(grad, i) in editColorGradients"
                  :id="getJoinedIds(grad.ids)"
                  :key="grad.ids[i]"
                  class="inputGroup gradientGroup"
                >
                  <input
                    v-for="(color, idx) in grad.colors"
                    :id="getJoinedIds(grad.ids) + idx"
                    :key="'vexColor_' + idx"
                    :data-groupid="getJoinedIds(grad.ids)"
                    :data-gradsinfo="getGradsInfo(idx, grad.ids)"
                    type="color"
                    :value="color"
                    @input="moveChangeGrad"
                    @change="changeGradient"
                  />
                </div>
              </div>
            </div>
            <canvas id="canvas" style="display: none"></canvas>
          </div>
          <div class="edit-actions d-flex">
            <button id="resetBtn" title="Reset" class="action-btn reset" @click="resetColor">
              <Icon
                class="mr-1"
                :width="iconSize"
                :height="iconSize"
                icon="reset"
                color="black"
              />{{ $t('main.preview.editColor.text3') }}
            </button>
            <button id="undoBtn" title="Undo" class="action-btn undo" @click="undoColor">
              <Icon :width="iconSize" :height="iconSize" icon="undo" color="black" />
            </button>
            <button id="redoBtn" title="Redo" class="action-btn redo" @click="redoColor">
              <Icon :width="iconSize" :height="iconSize" icon="redo" color="black" />
            </button>
          </div>
        </div>
      </div>
      <div class="editor-box m-auto col-12 min-h-0">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          v-if="showEditColor && !isLoadingSVG"
          id="downloadableSvg"
          class="canvas-container"
          v-html="svg"
        ></div>
        <b-spinner
          v-else-if="showEditColor && isLoadingSVG"
          variant="dark"
          class="d-block m-auto"
        ></b-spinner>
        <img
          v-show="!showEditColor"
          :src="vectorImage"
          class="img-fluid"
          :height="vectorHeight"
          :width="vectorWidth"
          :alt="`${altText} Transparent PNG`"
          @load="imgLoaded"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Icon from 'UI/Icon';
import {
  changeGrad,
  changeColorEvent,
  moveChangeColor,
  moveChangeGrad,
  reset,
  undo,
  redo
} from '~/utils/editColor.js';
export default {
  name: 'EditColor',
  components: {
    Icon
  },
  props: {
    vectorImage: { type: String, default: '' },
    vectorHeight: { type: Number, default: 250 },
    vectorWidth: { type: Number, default: 250 },
    altText: { type: String, default: '' }
  },

  data() {
    return {
      idGroups: '',
      iconSize: 25
    };
  },
  computed: {
    ...mapState('vectors', [
      'actualVector',
      'showEditColor',
      'editColorPalette',
      'editColorGradients',
      'isLoadingSVG'
    ]),
    svg() {
      return this.actualVector ? this.actualVector.svg : '';
    }
  },
  watch: {
    showEditColor(newValue) {
      if (newValue) {
        setTimeout(() => {
          this.resizeOpenEditor();
        }, 10);
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.resizeContainer);
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeContainer);
  },
  methods: {
    imgLoaded() {
      this.resizeContainer();
    },
    changeGradient(obj) {
      changeGrad(obj.target);
    },
    moveChangeColor(obj) {
      document.actualPicker = obj.target.id;
      moveChangeColor(obj.target.value);
    },
    moveChangeGrad(obj) {
      document.actualPicker = obj.target.id;
      moveChangeGrad(obj.target.value);
    },
    changeColor(obj) {
      document.actualPicker = obj.target.id;
      changeColorEvent(obj.target.value);
    },
    getJoinedIds(ids) {
      var result = 'vex';
      for (var i = 0; i < ids.length; i++) {
        result += ids[i];
      }
      return result;
    },
    getGradsInfo(idx, gradIds) {
      var result = '[' + idx + ',';
      for (var i = 0; i < gradIds.length; i++) {
        result += '"' + gradIds[i] + '"';
        if (i != gradIds.length - 1) {
          result += ',';
        }
      }
      result += ']';
      return result;
    },
    resetColor() {
      reset();
    },
    undoColor() {
      undo();
    },
    redoColor() {
      redo();
    },
    resizeOpenEditor() {
      this.resizeContainer();
      let containerSvg = document.getElementById('container-preview-svg');
      containerSvg.style.height = 'auto';
    },
    valuesResize() {
      let navHeight = document.getElementById('preview-nav')?.clientHeight;
      let navBottomHeight = document.getElementById('preview-bottom')?.clientHeight;
      let marginTop = 60;
      let marginBottom = 150;
      let total =
        navHeight && navBottomHeight ? navHeight + navBottomHeight + marginTop + marginBottom : 0;
      return total;
    },
    resizeContainer() {
      try {
        let totalHeight = this.valuesResize();
        if (totalHeight && window.innerWidth > 1199) {
          if (this.actualVector && this.actualVector.svg) {
            let containerPreviewSvg = document.getElementById('container-preview-svg');
            totalHeight += 500;
            containerPreviewSvg.style.height = 'auto';
            containerPreviewSvg.style.minHeight = `calc(100vh - ${totalHeight}px)`;
          } else {
            let containerImage = document.getElementById('container-preview-image');
            let imagePreview = document.getElementById('image-preview');

            containerImage.style.height = 'null';
            containerImage.style.width = 'auto';
            containerImage.style.height = `calc(100vh - ${totalHeight}px)`;
            containerImage.style.width = `${imagePreview.clientWidth}px`;
          }
        } else {
          const containerPreviewImage = document.getElementById('container-preview-image');
          if (!this.actualVector.svg && containerPreviewImage) {
            containerPreviewImage.style.height = 'auto';
            containerPreviewImage.style.width = 'auto';
            containerPreviewImage.style.width = `${
              document.getElementById('image-preview').clientWidth
            }px`;
          }
        }
      } catch (error) {
        console.error('Component Preview Carousel resizeContainer:', error);
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.container-svg {
  @include md {
    flex-direction: column;
  }
  flex-direction: row;
  display: flex;
  justify-content: center;
  align-items: center;

  .edit-color-container {
    @include md {
      justify-content: flex-start;
    }
    justify-content: flex-end;
    display: flex;
  }
  .controls-box {
    margin: 30px 0 0;
    @include md {
      margin: 15px 0 0;
    }
    width: min-content;
  }
  .color-editor-content {
    z-index: 10;
    padding: 40px 0;
    @include xl {
      padding: 20px 0;
    }
    @include md {
      flex-direction: column-reverse;
    }
    @include lg {
      padding: 75px 0 0;
    }
  }
}
.custom-tooltip {
  margin: 10px;
  .tooltip-inner {
    padding: 10px;
  }
}
.btn-tooltip {
  color: #fff;
  background: rgba(0, 0, 0, 0.16078);
  border-radius: 50px;
  padding: 3px 9px;
  font-size: 14px;
  margin-left: 10px;
  width: 22px;
  height: 22px;
  border: none;
}
.vx-container-colors {
  display: flex;
  flex-wrap: wrap;
}

.gradientGroup {
  background-color: #fff;
  display: inline-flex;
  padding: 12px;
  border-radius: 26px;
  margin-bottom: 26px;
  margin-right: 15px;
  display: flex;
  flex-wrap: wrap;
}

.inputGroup {
  margin-bottom: 20px;
}

.inputGroup input[type='color']:not(:last-child) {
  margin-right: 15px;
}

input[type='color'] {
  border-radius: 100%;
  padding: 0;
  height: 30px;
  width: 30px;
  border: 1px solid #ddd;
  outline: none;
  -webkit-appearance: none;
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  @include sm {
    margin-bottom: 10px;
  }
}

input[type='color']::-webkit-color-swatch-wrapper {
  padding: 0;
}
input[type='color']::-webkit-color-swatch {
  border: none;
  border-radius: 100%;
}
input[type='color']::-moz-color-swatch {
  border: none;
  border-radius: 100%;
}
.edit-title {
  font-size: 23px;
  font-weight: 500;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.edit-actions button {
  outline: none;
  border: none;
  background-color: #f6f6f6;
  font-size: 16px;
  font-weight: 500;
  margin-right: 15px;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  border-radius: 4px;
}

.gradient-group-title,
.color-group-title {
  font-size: 20px;
  font-weight: 400;
  margin-bottom: 15px;
}
.min-h-0 {
  min-height: 0;
}
.img-fluid {
  max-height: 100%;
  object-fit: contain;
}
</style>
